import {
  Alert,
  AlertType,
  Dialog,
  DialogMaxWidth,
  DialogVariant,
  Input,
  InputVariant,
  RadioGroup,
  Select,
  Text,
  TextHighlight,
  TextSize,
  TextWeight,
  textStyleUtils
} from '@focaldata/cin-ui-components'
import { FC, useState } from 'react'
import { KpiMetricType } from '../../../data/gql-gen/questionnaire/graphql'
import {
  DraftQuestionnaireEntry,
  EntryKpi
} from '../../../data/model/questionnaire'
import { useHasEarlierWaves } from './HasEarlierWavesContext'
import {
  KpiBaseField,
  KpiTickFields,
  METRIC_HEADING,
  getKpiFootnote,
  getMetricName
} from './KpiMeasureFields'
import useStyles from './KpiSettingsDialog.styles'
import {
  KPI_LABEL_MAX_LENGTH,
  KpiDialogModel,
  KpiDraft,
  canChooseKpiTopN,
  canSaveKpi,
  getDuplicateKpi,
  initialKpiDraft,
  isKpiMeasureChanged,
  isSavedKpiTopNAboveMax,
  setKpiDraftLabel,
  setKpiDraftMetric,
  setKpiDraftTopN,
  syncKpiDraftLabel,
  toKpiSaveInput
} from './kpiDialogModel'
import { getKpiRuleText, toKpiRuleInput } from './kpiRuleText'
import { KpiSaveInput } from './useKpiMutations'

// A KPI's N stays as saved, so an N above the places ranked now is still on offer.
const getTopNOptions = (model: KpiDialogModel) => {
  const values = Array.from({ length: model.maxTopN }, (_, index) => index + 1)
  const savedTopN = model.kpi?.topN ?? 1
  if (savedTopN > model.maxTopN) {
    values.push(savedTopN)
  }
  return values.map((value) => ({
    id: `${value}`,
    name: value === 1 ? '1 (first place)' : `${value}`,
    value
  }))
}

const getSavedTopNHint = (savedTopN: number, maxTopN: number) =>
  maxTopN > 1
    ? `This KPI counts the top ${savedTopN}, but the question now ranks only ${maxTopN} places. A new name keeps the top ${savedTopN}; any other change starts a new KPI, with N from 1 to ${maxTopN}.`
    : `This KPI counts the top ${savedTopN}, but the question now ranks only 1 place. A new name keeps the top ${savedTopN}; any other change starts a new KPI that counts first place.`

const getDuplicateMessage = (model: KpiDialogModel, duplicate: EntryKpi) => {
  const subject = model.rowId ? 'row' : 'question'
  const label = duplicate.label?.trim()
  const named = label ? `: "${label}"` : ''
  return `This ${subject} already has a KPI that counts exactly this${named}. Change what this one counts, or edit that KPI instead.`
}

const getNewKpiWarning = (hasEarlierWaves: boolean) =>
  hasEarlierWaves
    ? 'Changing what this KPI counts starts a new KPI. Its trend starts in this wave; earlier waves keep the old one.'
    : 'Changing what this KPI counts starts a new KPI.'

interface Props {
  entry: DraftQuestionnaireEntry
  model: KpiDialogModel
  saving: boolean
  onClose: () => void
  /** With replacedKpi, the input is a new KPI that takes the place of that one. */
  onSave: (input: KpiSaveInput, replacedKpi?: EntryKpi) => Promise<boolean>
}

const KpiSettingsDialog: FC<Props> = ({
  entry,
  model,
  saving,
  onClose,
  onSave
}) => {
  const { classes } = useStyles()
  const hasEarlierWaves = useHasEarlierWaves()
  const [draft, setDraft] = useState(() => initialKpiDraft(model))
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const { classes: textClasses, cx: classNames } =
    textStyleUtils.useTextStyles()

  const saveInput = toKpiSaveInput(model, draft)
  const isOneOption = !!model.fixedOption
  const showTicks =
    model.options.length > 0 && draft.metricType !== KpiMetricType.Average
  const ticksHeading = model.rowId
    ? 'Answer options that count in this row'
    : 'Options that count'
  const canChooseTopN = canChooseKpiTopN(model, draft)
  const isTopNFixed =
    draft.metricType === KpiMetricType.RankedTop &&
    model.maxTopN > 1 &&
    !canChooseTopN
  const savedTopN = model.kpi?.topN ?? 1
  const isSavedTopNAboveMax = isSavedKpiTopNAboveMax(model, draft)
  const isMeasureChanged = isKpiMeasureChanged(model, draft)
  const replacedKpi = isMeasureChanged ? model.kpi : undefined
  const duplicate = getDuplicateKpi(model, draft)
  const canSave = canSaveKpi(model, draft)
  const newKpiWarning = getNewKpiWarning(hasEarlierWaves)
  const closeConfirm = () => setIsConfirmOpen(false)
  const changeMeasure = (change: (current: KpiDraft) => KpiDraft) =>
    setDraft((current) => syncKpiDraftLabel(model, change(current)))

  return (
    <Dialog
      open
      title={model.title}
      onClose={onClose}
      fullWidth
      maxWidth={DialogMaxWidth.Sm}
      variant={DialogVariant.DoubleButton}
      secondaryButtonText="Cancel"
      secondaryButtonClick={onClose}
      primaryButtonText={
        replacedKpi && !duplicate ? 'Save as new KPI' : 'Save KPI'
      }
      primaryButtonClick={() =>
        replacedKpi ? setIsConfirmOpen(true) : onSave(saveInput)
      }
      primaryButtonDisabled={!canSave}
      primaryButtonLoading={saving}
    >
      <div className={classes.body}>
        <Text size={TextSize.ms} highlight={TextHighlight.Background}>
          {model.contextLine}
        </Text>

        {model.metricTypes.length > 1 ? (
          <RadioGroup
            caption={METRIC_HEADING}
            name="kpi-metric"
            value={draft.metricType}
            onChange={(_, value) =>
              setDraft((current) =>
                setKpiDraftMetric(model, current, value as KpiMetricType)
              )
            }
            items={model.metricTypes.map((metricType) => ({
              value: metricType,
              label: getMetricName(metricType, isOneOption)
            }))}
          />
        ) : (
          <div className={classes.section}>
            <Text size={TextSize.ms} weight={TextWeight.SemiBold}>
              {METRIC_HEADING}
            </Text>
            {canChooseTopN ? (
              <div className={classes.topN}>
                <Text size={TextSize.ms}>
                  {isOneOption
                    ? '% ranking this option in the top'
                    : '% ranking the ticked option in the top'}
                </Text>
                <Select
                  ariaLabel="Top N"
                  minWidth={72}
                  options={getTopNOptions(model)}
                  optionValue={draft.topN}
                  onChange={(event) => {
                    const topN = Number(event.target.value)
                    setDraft((current) => setKpiDraftTopN(model, current, topN))
                  }}
                />
              </div>
            ) : (
              <Text size={TextSize.ms}>
                {getMetricName(draft.metricType, isOneOption)}
              </Text>
            )}
            {isTopNFixed && (
              <Text size={TextSize.s} highlight={TextHighlight.Background}>
                Top N works with one ticked option, so this counts first place.
              </Text>
            )}
            {isSavedTopNAboveMax && (
              <Text size={TextSize.s} highlight={TextHighlight.Background}>
                {getSavedTopNHint(savedTopN, model.maxTopN)}
              </Text>
            )}
          </div>
        )}

        {showTicks && (
          <KpiTickFields
            subjectKind={model.subjectKind}
            options={model.options}
            draft={draft}
            heading={ticksHeading}
            onChange={changeMeasure}
          />
        )}

        {draft.metricType !== KpiMetricType.Average && (
          <KpiBaseField
            subject={model.rowId ? 'row' : 'question'}
            draft={draft}
            onChange={changeMeasure}
          />
        )}

        <div className={classes.section}>
          <Text size={TextSize.ms} weight={TextWeight.SemiBold}>
            Name
          </Text>
          <Input
            variant={InputVariant.Filled}
            fullWidth
            value={draft.label}
            onChange={(event) => {
              const { value } = event.target
              setDraft((current) => setKpiDraftLabel(current, value))
            }}
            ariaLabel="KPI name"
            maxLength={KPI_LABEL_MAX_LENGTH}
            placeholder="e.g. Optimism"
          />
          <Text size={TextSize.s} highlight={TextHighlight.Background}>
            Shown on the card and, later, in KPI results
          </Text>
        </div>

        <div className={classes.preview}>
          <Text size={TextSize.ms}>
            <strong>{draft.label.trim() || 'This KPI'}</strong> ={' '}
            {getKpiRuleText(
              toKpiRuleInput(saveInput, entry),
              model.questionRef
            )}
          </Text>
        </div>

        {duplicate && (
          <Alert severity={AlertType.WARNING}>
            {getDuplicateMessage(model, duplicate)}
          </Alert>
        )}

        {isMeasureChanged && !duplicate && (
          <Alert severity={AlertType.WARNING}>{newKpiWarning}</Alert>
        )}

        <Text size={TextSize.s} highlight={TextHighlight.Background}>
          {getKpiFootnote(model, hasEarlierWaves)}
        </Text>
      </div>
      <Dialog
        title="Start a new KPI?"
        open={isConfirmOpen}
        onClose={closeConfirm}
        onClickOutside={closeConfirm}
        variant={DialogVariant.DoubleButton}
        primaryButtonText="Start new KPI"
        primaryButtonClick={async () => {
          if (replacedKpi && !(await onSave(saveInput, replacedKpi))) {
            closeConfirm()
          }
        }}
        primaryButtonDisabled={!replacedKpi || !canSave}
        primaryButtonLoading={saving}
        secondaryButtonText="Back"
        secondaryButtonClick={closeConfirm}
      >
        <p
          className={classNames(
            textClasses.default,
            textClasses.weightSemiBold
          )}
        >
          {newKpiWarning}
        </p>
      </Dialog>
    </Dialog>
  )
}

export default KpiSettingsDialog
